/**
 * The paper the QR poster is printed on, shared between the sheet itself and
 * the page that previews it — the preview scales real millimetres down to
 * whatever width its column has, so it has to know the same numbers.
 */

/** Which size the poster is cut down to. */
export type QrPosterFormat = 'a6' | 'a5' | 'a4';

/** The sheet the poster is designed at — an A5, in millimetres. */
export const SHEET_WIDTH_MM = 148;
export const SHEET_HEIGHT_MM = 210;

/**
 * White left around the outside of the grid. It doubles as insurance: no
 * consumer printer reaches the edge of the paper, and a design that ran to it
 * would come back trimmed.
 */
export const PAGE_MARGIN_MM = 6;

/**
 * White left between the copies, so each one is cut along its own line rather
 * than along one shared with its neighbour. The scissors get 4mm of room on
 * either side, which is what makes a stack of cut-and-laminated cards come out
 * the same size as each other.
 */
export const GUTTER_MM = 8;

export interface PosterFormatSpec {
  pageWidthMm: number;
  pageHeightMm: number;
  /** What the print document asks the printer for. */
  pageSize: string;
  /** How the copies are tiled across the printed sheet. */
  columns: number;
  rows: number;
}

/**
 * Every format fills one sheet of A4; what changes is how many copies come off
 * it. The A-series' √2 is what lets a single design cover all three — an A5
 * halves into two A6 and doubles into an A4 without changing shape — so the
 * poster is drawn once and scaled to whatever cell it lands in.
 *
 * The copies come out a little under their nominal size: the margins and the
 * gutters have to come out of the same sheet of A4, and a true A5 pair would
 * need all 297mm of it with nothing left to cut along.
 */
export const POSTER_FORMATS: Record<QrPosterFormat, PosterFormatSpec> = {
  a4: { pageWidthMm: 210, pageHeightMm: 297, pageSize: 'A4 portrait', columns: 1, rows: 1 },
  // Two portrait copies sit side by side only across an A4 turned on its side.
  a5: { pageWidthMm: 297, pageHeightMm: 210, pageSize: 'A4 landscape', columns: 2, rows: 1 },
  a6: { pageWidthMm: 210, pageHeightMm: 297, pageSize: 'A4 portrait', columns: 2, rows: 2 },
};

/** Where one copy sits on the page, in millimetres. */
export interface PosterCopyRect {
  leftMm: number;
  topMm: number;
}

export interface PosterLayout {
  spec: PosterFormatSpec;
  /** What the A5 design is multiplied by to fill its cell. */
  scale: number;
  /** The size one cut-out copy comes to. */
  sheetWidthMm: number;
  sheetHeightMm: number;
  /** One entry per copy, in reading order. */
  copies: PosterCopyRect[];
}

/**
 * Fits the copies onto the page: the largest the A5 design can be drawn at
 * once the margins and gutters are taken out, tiled and centred.
 */
export function posterLayout(format: QrPosterFormat): PosterLayout {
  const spec = POSTER_FORMATS[format];
  const { columns, rows } = spec;

  const usableWidth = spec.pageWidthMm - 2 * PAGE_MARGIN_MM - (columns - 1) * GUTTER_MM;
  const usableHeight = spec.pageHeightMm - 2 * PAGE_MARGIN_MM - (rows - 1) * GUTTER_MM;

  const scale = Math.min(usableWidth / columns / SHEET_WIDTH_MM, usableHeight / rows / SHEET_HEIGHT_MM);
  const sheetWidthMm = SHEET_WIDTH_MM * scale;
  const sheetHeightMm = SHEET_HEIGHT_MM * scale;

  // Centre the block of copies, so whatever the fit left over is shared evenly
  // between the two margins rather than piling up on one side.
  const gridWidth = columns * sheetWidthMm + (columns - 1) * GUTTER_MM;
  const gridHeight = rows * sheetHeightMm + (rows - 1) * GUTTER_MM;
  const originX = (spec.pageWidthMm - gridWidth) / 2;
  const originY = (spec.pageHeightMm - gridHeight) / 2;

  const copies = Array.from({ length: columns * rows }, (_, index) => ({
    leftMm: originX + (index % columns) * (sheetWidthMm + GUTTER_MM),
    topMm: originY + Math.floor(index / columns) * (sheetHeightMm + GUTTER_MM),
  }));

  return { spec, scale, sheetWidthMm, sheetHeightMm, copies };
}

/** How many copies one printed sheet yields. */
export function copiesPerSheet(format: QrPosterFormat): number {
  const { columns, rows } = POSTER_FORMATS[format];
  return columns * rows;
}

/** CSS millimetres are fixed at 96dpi, which is what scales the preview. */
export const PX_PER_MM = 96 / 25.4;
